import React, { useEffect, useState } from "react";
import axios from "axios";
import { StudyMaterial, StudyMaterialFile, PreSignedVideoUploadDetails } from "../../../../types/dataModel";
import { getAPIHeaders, getAPIMultiPartHeaders, getVideoDuration, generateVideoThumbnail } from "../../../../common/Utils";
import useFetch from "../../../../hooks/useFetch";
import { API } from "../../../../common/Constants";
import FormFooter from "../../../../core/common/formFooter";
import PreLoader from "../../../../core/common/preLoader";
import CustomToast from "../../../../core/common/customToast";
import VideoMaterialUploadSection, { VideoMaterialDraft, VideoUploadMode } from "./videoMaterialUploadSection";

const LARGE_VIDEO_SIZE_BYTES = 0;

interface Props {
  syllabusId: number;
  studyMaterial?: StudyMaterial | null;
  nodeData: any;
  isPublic: boolean;
  onIsPublicChange: (value: boolean) => void;
  onSuccess: () => void;
  onCancel: () => void;
}

const VideoMaterialFormSection: React.FC<Props> = ({
  syllabusId,
  studyMaterial,
  nodeData,
  isPublic,
  onIsPublicChange,
  onSuccess,
  onCancel,
}) => {
  const [videoUploadMode, setVideoUploadMode] = useState<VideoUploadMode>("single");
  const [singleTitle, setSingleTitle] = useState<string>("");
  const [selectedFiles, setSelectedFiles] = useState<StudyMaterialFile[]>([]);
  const [multiVideoMaterials, setMultiVideoMaterials] = useState<VideoMaterialDraft[]>([]);
  const [multiVideoTitleErrors, setMultiVideoTitleErrors] = useState<Record<number, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [multiUploadProgress, setMultiUploadProgress] = useState<Record<number, number>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Set<number>>(new Set());
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [selectedThumbnailFile, setSelectedThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "Something went wrong please try again later",
    heading: "Oops! Something went wrong",
    type: "ERROR",
  });

  const { isLoading, error, callFetch } = useFetch();

  useEffect(() => {
    if (error?.response?.data) {
      setToastSettings((prev) => ({
        ...prev,
        show: true,
        message: error.response?.data || "Something went wrong, please try again later.",
        heading: "Error",
        type: "ERROR",
      }));
    }
  }, [error]);

  useEffect(() => {
    if (studyMaterial) {
      setSingleTitle(studyMaterial.title || "");
      setSelectedFiles(studyMaterial.files || []);
      setVideoUploadMode("single");
      setMultiVideoMaterials([]);
      setMultiVideoTitleErrors({});
      setIsPreview(studyMaterial.metadata?.preview === true);
      // Set existing thumbnail preview if available
      if (studyMaterial.metadata?.thumbnail_url) {
        setThumbnailPreview(studyMaterial.metadata.thumbnail_url);
      } else {
        setThumbnailPreview(null);
      }
    } else {
      setSingleTitle("");
      setSelectedFiles([]);
      setVideoUploadMode("single");
      setMultiVideoMaterials([]);
      setMultiVideoTitleErrors({});
      setIsPreview(false);
      setThumbnailPreview(null);
    }

    setSelectedThumbnailFile(null);
    setUploadProgress(0);
    setMultiUploadProgress({});
    setUploadedFiles(new Set());
    setIsUploading(false);
  }, [studyMaterial]);

  const getDefaultTitleFromFile = (file: StudyMaterialFile) => {
    const rawName = file.name || file.fileName || "";
    if (!rawName) {
      return "";
    }
    const lastDotIndex = rawName.lastIndexOf(".");
    if (lastDotIndex > 0) {
      return rawName.substring(0, lastDotIndex);
    }
    return rawName;
  };

  const getFileSizeInBytes = (file: StudyMaterialFile) => file.size ?? (file as any)?.fileSize ?? 0;

  const requiresPresignedUpload = (file: StudyMaterialFile) => getFileSizeInBytes(file) > LARGE_VIDEO_SIZE_BYTES;

  // Convert base64 thumbnail to File/Blob for upload
  const base64ToFile = (base64: string, fileName: string, mimeType: string = "image/jpeg"): File => {
    const base64Data = base64.split(",")[1] || base64;
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    return new File([blob], fileName, { type: mimeType });
  };

  // Upload thumbnail using API.ADD_FILES
  const uploadThumbnail = async (thumbnailBase64: string, videoFileName: string): Promise<any | null> => {
    try {
      const thumbnailFile = base64ToFile(
        thumbnailBase64,
        `${videoFileName}_thumbnail.jpg`,
        "image/jpeg"
      );

      const formData = new FormData();
      formData.append("files", thumbnailFile);
      formData.append("isPublic", "true");

      const response = await callFetch(
        API.ADD_FILES,
        "POST",
        getAPIMultiPartHeaders(),
        null,
        formData
      );

      // API.ADD_FILES returns an array, get the first file's id
      if (Array.isArray(response) && response.length > 0 && response[0].id) {
        return  response[0] as any;
      }
      return null;
    } catch (error) {
      console.error("Error uploading thumbnail:", error);
      return null;
    }
  };

  // Delete thumbnail using API.FILES
  const deleteThumbnail = async (thumbnailFileId: number): Promise<void> => {
    try {
      await callFetch(
        API.FILES.replace(":fileId", thumbnailFileId.toString()),
        "DELETE",
        getAPIHeaders(),
        null,
        null
      );
    } catch (error) {
      console.error("Error deleting thumbnail:", error);
      // Don't throw - continue with file deletion even if thumbnail deletion fails
    }
  };

  const getVideoScopePayload = () => {
    const payload: {
      syllabusId: number;
      unitId?: number;
      sectionId?: number;
    } = { syllabusId };

    if (nodeData?.nodeType === "UNIT" && nodeData?.id) {
      payload.unitId = nodeData.id;
    }
    if (nodeData?.nodeType === "SECTION" && nodeData?.id) {
      payload.sectionId = nodeData.id;
    }

    return payload;
  };

  const requestUploadDetails = async (file: StudyMaterialFile) => {
    const body = {
      ...getVideoScopePayload(),
      fileName: file.name || (file as any)?.fileName || "video",
      fileSizeBytes: getFileSizeInBytes(file),
      materialType: "VIDEO",
    };

    const response: PreSignedVideoUploadDetails = await callFetch(
      API.GET_UPLOAD_URL_STUDY_MATERIAL,
      "POST",
      getAPIHeaders(),
      null,
      body
    );

    return response;
  };

  const confirmUpload = async (details: PreSignedVideoUploadDetails, title: string, duration?: number | null, thumbnailFile?: any | null) => {
    const payload: any = {
      id: studyMaterial?.id ? studyMaterial.id : null,
      studyMaterialId: studyMaterial?.id ? studyMaterial.id : null,
      uploadId: details.uploadId,
      fileKey: details.fileKey,
      ...getVideoScopePayload(),
      title,
      content: "",
      materialType: "VIDEO",
      isPublic: isPublic || false,
    };

    const metadata: any = {};
    if (isPreview) {
      metadata.preview = true;
    } else {
      metadata.preview = false;
    }
    if (duration !== null && duration !== undefined) {
      metadata.duration = duration;
    }
    if (thumbnailFile) {
      metadata.thumbnail_file_id = thumbnailFile.id;
      metadata.thumbnail_url = thumbnailFile.fileUrl;
    }
    payload.metadata = metadata;
    if(studyMaterial?.id){
      studyMaterial.metadata = {
        ...studyMaterial.metadata,
        ...metadata,
      };
    }
    await callFetch(API.CONFIRM_UPLOAD_URL_STUDY_MATERIAL, "POST", getAPIHeaders(), null, payload);
  };

  const uploadViaPresignedUrl = async (
    file: StudyMaterialFile,
    title: string,
    onProgress?: (percent: number) => void
  ) => {
    try {
      // Upload thumbnail first
      const thumbnail = (file as any).thumbnail;
      let thumbnailFile: any | null = null;
      if (thumbnail) {
        const videoFileName = file.name || (file as any)?.fileName || "video";
        thumbnailFile = await uploadThumbnail(thumbnail, videoFileName);
      }

      const uploadDetails = await requestUploadDetails(file);
      setIsUploading(true);
      await axios.put(uploadDetails.uploadUrl, file, {
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        onUploadProgress: (event) => {
          if (event.total) {
            const percentCompleted = Math.round((event.loaded * 100) / event.total);
            onProgress?.(percentCompleted);
          }
        },
      });
      const duration = (file as any).videoDuration;
      await confirmUpload(uploadDetails, title, duration, thumbnailFile);
    } finally {
      setIsUploading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    let hasMultiVideoErrors = false;

    if (!(formIsMultiMode() && !studyMaterial?.id)) {
      if (!singleTitle?.trim()) {
        newErrors.title = "Title is required";
      }
    }

    if (!studyMaterial?.id && formIsMultiMode()) {
      if (multiVideoMaterials.length === 0) {
        newErrors.files = "Please select at least one video file";
      }
      const titleErrors: Record<number, string> = {};
      multiVideoMaterials.forEach((material, index) => {
        if (!material.title?.trim()) {
          titleErrors[index] = "Title is required";
        }
      });
      setMultiVideoTitleErrors(titleErrors);
      if (Object.keys(titleErrors).length > 0) {
        hasMultiVideoErrors = true;
      }
    } else if (selectedFiles.length === 0 && !studyMaterial) {
      newErrors.files = "Please select at least one video file";
      setMultiVideoTitleErrors({});
    } else {
      setMultiVideoTitleErrors({});
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && !hasMultiVideoErrors;
  };

  const formIsMultiMode = () => videoUploadMode === "multiple";

  const handleVideoFilesChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []) as StudyMaterialFile[];
    if (files.length === 0) {
      return;
    }

    // In single mode, only take the first file
    const filesToProcess = formIsMultiMode() ? files : [files[0]];

    // Extract video durations and generate thumbnails for all files
    const filesWithMetadata = await Promise.all(
      filesToProcess.map(async (file) => {
        const [duration, thumbnail] = await Promise.all([
          getVideoDuration(file),
          generateVideoThumbnail(file, {
            timePosition: 2,
            width: 1280,
            height: 720,
            quality: 0.85,
          }),
        ]);
        // Store duration and thumbnail as custom properties on the file object
        (file as any).videoDuration = duration;
        (file as any).thumbnail = thumbnail;
        return file;
      })
    );

    if (!studyMaterial?.id && formIsMultiMode()) {
      setMultiVideoMaterials((prev) => {
        const updated = [
          ...prev,
          ...filesWithMetadata.map((file) => ({
            file,
            title: getDefaultTitleFromFile(file),
          })),
        ];
        setMultiVideoTitleErrors(() => {
          const newErrors: Record<number, string> = {};
          updated.forEach((material, idx) => {
            if (!material.title?.trim()) {
              newErrors[idx] = "Title is required";
            }
          });
          return newErrors;
        });
        return updated;
      });
      setSelectedFiles([]);
      // Clear uploaded files when new files are added
      setUploadedFiles(new Set());
    } else {
      // In single mode, replace existing files with the new one
      // In multiple mode (when editing), append files
      setSelectedFiles(formIsMultiMode() ? (prev) => [...prev, ...filesWithMetadata] : filesWithMetadata);
    }
    setErrors((prev) => ({ ...prev, files: "" }));
    event.target.value = "";
  };

  const handleMultiVideoRemove = (index: number) => {
    setMultiVideoMaterials((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      setMultiVideoTitleErrors((prevErrors) => {
        if (Object.keys(prevErrors).length === 0) {
          return prevErrors;
        }
        const newErrors: Record<number, string> = {};
        updated.forEach((material, idx) => {
          const sourceIndex = idx >= index ? idx + 1 : idx;
          if (prevErrors[sourceIndex]) {
            newErrors[idx] = prevErrors[sourceIndex];
          } else if (!material.title.trim()) {
            newErrors[idx] = "Title is required";
          }
        });
        return newErrors;
      });
      // Adjust uploadedFiles indices - remove the deleted index and shift others
      setUploadedFiles((prev) => {
        const newSet = new Set<number>();
        prev.forEach((uploadedIndex) => {
          if (uploadedIndex < index) {
            newSet.add(uploadedIndex);
          } else if (uploadedIndex > index) {
            newSet.add(uploadedIndex - 1);
          }
          // If uploadedIndex === index, don't add it (file was removed)
        });
        return newSet;
      });
      return updated;
    });
  };

  const handleVideoUploadModeChange = (mode: VideoUploadMode) => {
    setVideoUploadMode(mode);
    setErrors((prev) => ({ ...prev, files: "", title: "" }));
    if (mode === "single") {
      setMultiVideoMaterials([]);
      setMultiVideoTitleErrors({});
      setUploadedFiles(new Set());
    } else {
      setSelectedFiles([]);
      setSingleTitle("");
      setUploadedFiles(new Set());
    }
  };

  const handleMultiVideoTitleChange = (index: number, value: string) => {
    setMultiVideoMaterials((prev) => prev.map((material, idx) => (idx === index ? { ...material, title: value } : material)));
    setMultiVideoTitleErrors((prev) => {
      const updated = { ...prev };
      if (value.trim()) {
        delete updated[index];
      } else {
        updated[index] = "Title is required";
      }
      return updated;
    });
  };

  const removeFile = (file: StudyMaterialFile, index: number) => {
    if (file.id) {
      handleDeleteFile(file.id, index);
    } else {
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleDeleteFile = async (fileJunctionId: number, index: number) => {
    try {
      // Delete thumbnail if it exists in studyMaterial metadata
      if (studyMaterial?.metadata?.thumbnail_file_id) {
        const thumbnailFileId = studyMaterial.metadata.thumbnail_file_id;
        await deleteThumbnail(thumbnailFileId);
        studyMaterial.metadata = {
          ...studyMaterial.metadata,
          thumbnail_file_id: null,
          thumbnail_url: null,
        }
        setThumbnailPreview(null);
      }

      // Delete the file
      await callFetch(API.REMOVE_FILE_FROM_STUDY_MATERIAL.replace(":fileId", fileJunctionId.toString()), "DELETE", getAPIHeaders(), null, null);
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  };

  const handleThumbnailFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setToastSettings({
          show: true,
          message: "Please select an image file for thumbnail",
          heading: "Invalid File Type",
          type: "ERROR",
        });
        event.target.value = "";
        return;
      }

      setSelectedThumbnailFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
    event.target.value = "";
  };

  const handleRemoveThumbnail = async () => {
    // If there's an existing thumbnail, delete it
    if (studyMaterial?.metadata?.thumbnail_file_id) {
      try {
        await deleteThumbnail(studyMaterial.metadata.thumbnail_file_id);
      } catch (error) {
        console.error("Error deleting existing thumbnail:", error);
      }
    }
    setSelectedThumbnailFile(null);
    setThumbnailPreview(null);
  };

  const handleAddFilesToMaterial = async () => {
    if (studyMaterial?.id && selectedFiles.length) {
      const newFiles = selectedFiles.filter((file) => !file.id);
      if (!newFiles.length) {
        return Promise.resolve();
      }

      const presignedFiles = newFiles.filter((file) => requiresPresignedUpload(file));
      // const regularFiles = newFiles.filter((file) => !requiresPresignedUpload(file));

      setIsUploading(true);
      setUploadProgress(0);

      try {
        for (const file of presignedFiles) {
          const derivedTitle = getDefaultTitleFromFile(file);
          await uploadViaPresignedUrl(file, derivedTitle, (percent) => setUploadProgress(percent));
          setUploadProgress(100);
        }
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
      return Promise.resolve();
    }
    return Promise.resolve();
  };

  const uploadMultipleVideoMaterials = async () => {
    setIsUploading(true);

    try {
      for (let index = 0; index < multiVideoMaterials.length; index++) {
        const material = multiVideoMaterials[index];
        const derivedTitle = material.title?.trim() || getDefaultTitleFromFile(material.file) || "";
        const usePresigned = requiresPresignedUpload(material.file);

        setMultiUploadProgress((prev) => ({ ...prev, [index]: 0 }));

        // Upload thumbnail first
        // const thumbnail = (material.file as any).thumbnail;
        // let thumbnailFile: any | null = null;
        // if (thumbnail) {
        //   const videoFileName = material.file.name || (material.file as any)?.fileName || "video";
        //   thumbnailFile = await uploadThumbnail(thumbnail, videoFileName);
        // }

        if (usePresigned) {
          await uploadViaPresignedUrl(material.file, derivedTitle, (percent) =>
            setMultiUploadProgress((prev) => ({ ...prev, [index]: percent }))
          );
          setMultiUploadProgress((prev) => ({ ...prev, [index]: 100 }));
          setUploadedFiles((prev) => new Set(prev).add(index));
          continue;
        }

      }
    } finally {
      setIsUploading(false);
      setMultiUploadProgress({});
    }
  };

  const handleSingleVideoUpload = async () => {
    if (selectedFiles.length === 0) {
      setErrors((prev) => ({ ...prev, files: "Please select at least one video file" }));
      return;
    }

    const file = selectedFiles[0];
    const derivedTitle = singleTitle || getDefaultTitleFromFile(file);
    const usePresigned = requiresPresignedUpload(file);

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Upload thumbnail first
      // const thumbnail = (file as any).thumbnail;
      // let thumbnailFileId: number | null = null;
      // if (thumbnail) {
      //   const videoFileName = file.name || (file as any)?.fileName || "video";
      //   thumbnailFileId = await uploadThumbnail(thumbnail, videoFileName);
      // }

      if (usePresigned) {
        await uploadViaPresignedUrl(file, derivedTitle, (percent) => setUploadProgress(percent));
        setUploadProgress(100);
        return;
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const updateStudyMaterial = async () => {
    if (!studyMaterial?.id) {
      return;
    }
    const updatePayload: any = {
      title: singleTitle,
      content: studyMaterial.content || "",
      materialType: "VIDEO",
      isPublic: isPublic || false,
    };

    if (isPreview) {
      updatePayload.metadata = {
        ...studyMaterial.metadata,
        preview: true,
      };
    } else {
      updatePayload.metadata = {
        ...studyMaterial.metadata,
        preview: false,
      };
    }

    await callFetch(
      API.UPDATE_STUDY_MATERIAL.replace(":id", studyMaterial.id.toString()),
      "PUT",
      getAPIHeaders(),
      null,
      updatePayload
    );
  }

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (studyMaterial?.id) {
        // Upload thumbnail if a new one was selected
        let thumbnailFile: any | null = null;
        if (selectedThumbnailFile && thumbnailPreview) {
          // thumbnailPreview is already a base64 data URL from handleThumbnailFileChange
          // Use the file name or a default name
          const fileName = selectedThumbnailFile.name || studyMaterial.title || "thumbnail";
          const videoFileName = fileName.replace(/\.[^/.]+$/, ""); // Remove extension
          thumbnailFile = await uploadThumbnail(thumbnailPreview, videoFileName);
          // Delete old thumbnail if it exists
          if (studyMaterial.metadata?.thumbnail_file_id) {
            await deleteThumbnail(studyMaterial.metadata.thumbnail_file_id);
          }
          if (thumbnailFile) {
            studyMaterial.metadata = { ...studyMaterial.metadata, 
              thumbnail_file_id : thumbnailFile.id,
              thumbnail_url : thumbnailFile.fileUrl
            }
          }
        }

        // Include thumbnail in metadata if available

        await handleAddFilesToMaterial();
        await updateStudyMaterial();
        onSuccess();
      } else {
        if (formIsMultiMode()) {
          await uploadMultipleVideoMaterials();
          onSuccess();
        } else {
          await handleSingleVideoUpload();
          onSuccess();
        }
      }
    } catch (submissionError) {
      console.error("Error saving video study material:", submissionError);
    }
  };

  return (
    <>
      <PreLoader showLoader={isLoading || isUploading} />
      <CustomToast
        show={toastSettings.show}
        message={toastSettings.message}
        heading={toastSettings.heading}
        type={toastSettings.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />

      <form onSubmit={handleSubmit}>
        <VideoMaterialUploadSection
          mode={videoUploadMode}
          onModeChange={handleVideoUploadModeChange}
          isEditing={!!studyMaterial?.id}
          singleTitle={singleTitle}
          onSingleTitleChange={(value) => {
            setSingleTitle(value);
            if (errors.title) setErrors((prev) => ({ ...prev, title: "" }));
          }}
          singleTitleError={errors.title}
          files={selectedFiles}
          onFilesSelected={handleVideoFilesChange}
          onRemoveFile={removeFile}
          filesError={errors.files}
          multiVideoMaterials={multiVideoMaterials}
          multiTitleErrors={multiVideoTitleErrors}
          onMultiTitleChange={handleMultiVideoTitleChange}
          onMultiRemove={handleMultiVideoRemove}
          uploadProgress={uploadProgress}
          multiUploadProgress={multiUploadProgress}
          isUploading={isUploading}
          uploadedFiles={uploadedFiles}
          existingThumbnailUrl={thumbnailPreview || studyMaterial?.metadata?.thumbnail_url || null}
          onThumbnailFileChange={handleThumbnailFileChange}
          onRemoveThumbnail={handleRemoveThumbnail}
        />

        <div className="mb-3">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="videoIsPublic"
              checked={isPublic}
              onChange={(e) => onIsPublicChange(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="videoIsPublic">
              Make this material public
            </label>
          </div>
          <div className="form-text">Public materials are visible to all students. Private materials are only visible to faculty.</div>
        </div>

        <div className="mb-3">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="videoIsPreview"
              checked={isPreview}
              onChange={(e) => setIsPreview(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="videoIsPreview">
              Preview
            </label>
          </div>
          <div className="form-text">Preview videos will be accessible to all end users.</div>
        </div>

        <FormFooter onCancel={onCancel} onSave={handleSubmit} readonly={isLoading} noBootstrap={true} />
      </form>
    </>
  );
};

export default VideoMaterialFormSection;

